import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { getKeywordsItems } from "app/components/React/QuickSearch/helpers/getKeywordsItems";
import { getWebsitesItems } from "app/components/React/QuickSearch/helpers/getWebsitesItems";
import { navigateToQuickSearchPageUrl } from "app/components/React/QuickSearch/helpers/navigateToQuickSearchPageUrl";
import { ItemTabs } from "app/components/React/QuickSearch/ItemTabs/ItemTabs";
import type { QuickSearchCatalogPage } from "app/components/React/QuickSearch/pageData/getQuickSearchCatalogPages";
import { openLockedFeatureHook } from "app/components/React/QuickSearch/pageData/openLockedFeatureHook";
import { useAccessibleQuickSearchCatalogPages } from "app/components/React/QuickSearch/pageData/useAccessibleQuickSearchCatalogPages";
import { QuickSearchAllTabPanel } from "app/components/React/QuickSearch/QuickSearchAllTabPanel";
import { QuickSearchReportRow } from "app/components/React/QuickSearch/QuickSearchReportRow";
import { QuickSearchReportsTabPanel } from "app/components/React/QuickSearch/QuickSearchReportsTabPanel";
import { QuickSearchTabEmptyState } from "app/components/React/QuickSearch/QuickSearchTabEmptyState";
import { SearchTabs } from "app/components/React/QuickSearch/types";
import {
    ALL_PLACEHOLDER,
    FALLBACK_PLACEHOLDER,
    KEYWORD_PLACEHOLDER,
    REPORTS_PLACEHOLDER,
    WEBSITE_PLACEHOLDER,
} from "app/components/React/SearchAutocompleteWithTabs/texts";
import type { Recent } from "app/custom-hooks/useRecentsHook";
import { translate } from "app/filters/i18nFilter";
import type { OnSearchItemClick } from "app/pages/activation/components/HomeSearch/types";
import { getIsKeywordsLocked, getIsWebsitesLocked } from "app/pages/activation/helpers/getIsLockedComponent";

import { REPORTS_TAB_ITEM_COUNT, TAB_LIST } from "../QuickSearch/config";
import { quickSearchDataAutomation } from "../QuickSearch/dataAutomation";
import { useRecentQuery } from "../QuickSearch/queries/useRecentQuery";
import quickSearchTracking from "../QuickSearch/tracking";

const MIN_TRACKED_QUERY_LENGTH = 3;

const navigateCatalogPage = (page: QuickSearchCatalogPage): void => {
    if (page.isLocked) {
        openLockedFeatureHook(page.uiRouterState, page.hookKey);

        return;
    }

    // External pages (e.g. account/API links) render as real anchors that open a new tab
    // via their `target="_blank"` reference, so only in-app pages navigate programmatically.
    if (!page.isExternal) {
        navigateToQuickSearchPageUrl(page.url, page.title);
    }
};

type UseSearchAutocompleteWithTabsProps = {
    isOpen: boolean;
    onClose: () => void;
    onKeywordClick?: OnSearchItemClick;
    onWebsiteClick?: OnSearchItemClick;
    onReportClick?: OnSearchItemClick;
    onTabClick?: (tabName: string) => void;
    /**
     * Override for user-query tracking. When provided, replaces the default Quick Search
     * tracking so each consumer can use its own analytics category.
     */
    onTrackUserQuery?: (query: string, tab: string) => void;
    /** Max height (px) of the scroll region under the tab strip (see {@link ItemTabs}). */
    tabPanelScrollMaxHeightPx: number;
    /** Min height (px) of the scroll region. When equal to max, the panel has a fixed height. */
    tabPanelScrollMinHeightPx?: number;
};

export const useSearchAutocompleteWithTabs = ({
    isOpen,
    onClose,
    onKeywordClick,
    onWebsiteClick,
    onReportClick,
    onTabClick,
    onTrackUserQuery,
    tabPanelScrollMaxHeightPx,
    tabPanelScrollMinHeightPx,
}: UseSearchAutocompleteWithTabsProps) => {
    const { getLocale } = useTranslate();
    const isKeywordsLocked = getIsKeywordsLocked();
    const isWebsitesLocked = getIsWebsitesLocked();
    const [tabIndex, setTabIndex] = useState(0);
    const [listScrollResetKey, setListScrollResetKey] = useState("");
    const [isRecentReady, setIsRecentReady] = useState(false);
    const latestSearchRef = useRef<{ query: string; tab: string } | null>(null);
    const lastTrackedQueryRef = useRef<string | null>(null);

    const {
        recents: recentWebsitesRaw,
        refetch: refetchWS,
        isRecentLoading: isWsLoading,
    } = useRecentQuery("website", true);

    const {
        recents: recentKeywordsRaw,
        refetch: refetchKW,
        isRecentLoading: isKwLoading,
    } = useRecentQuery("keyword", true);

    const {
        recents: recentAllRaw,
        refetch: refetchAll,
        isRecentLoading: isAllLoading,
    } = useRecentQuery(undefined, true);

    const recentWebsites = useMemo<Recent[]>(() => (recentWebsitesRaw ?? []).slice(), [recentWebsitesRaw]);

    const recentKeywords = useMemo<Recent[]>(() => (recentKeywordsRaw ?? []).slice(), [recentKeywordsRaw]);

    const recentAll = useMemo<Recent[]>(() => (recentAllRaw ?? []).slice(), [recentAllRaw]);

    const recentsVersion = useMemo(
        () => `${recentWebsites.length}-${recentKeywords.length}-${recentAll.length}`,
        [recentWebsites.length, recentKeywords.length, recentAll.length],
    );

    useEffect(() => {
        if (!isOpen) {
            setTabIndex(0);
            setListScrollResetKey("");
        }
    }, [isOpen]);

    useEffect(() => {
        let canceled = false;

        const run = async () => {
            if (!isOpen) {
                setIsRecentReady(false);
                return;
            }

            setIsRecentReady(false);
            try {
                await Promise.all([refetchWS(), refetchKW(), refetchAll()]);
            } finally {
                if (!canceled) {
                    setIsRecentReady(true);
                }
            }
        };

        void run();

        return () => {
            canceled = true;
        };
    }, [isOpen, refetchKW, refetchWS, refetchAll]);

    useEffect(() => {
        if (!isOpen) {
            latestSearchRef.current = null;
            lastTrackedQueryRef.current = null;
        }
    }, [isOpen]);

    const getPlaceholder = (): string => {
        const placeholders: Record<SearchTabs, string> = {
            [SearchTabs.All]: translate(ALL_PLACEHOLDER),
            [SearchTabs.Website]: translate(WEBSITE_PLACEHOLDER),
            [SearchTabs.Keyword]: translate(KEYWORD_PLACEHOLDER),
            [SearchTabs.Reports]: translate(REPORTS_PLACEHOLDER),
        };
        return placeholders[TAB_LIST[tabIndex]] ?? translate(FALLBACK_PLACEHOLDER);
    };

    const onKeywordItemClick = useCallback<OnSearchItemClick>(
        (keyword, state) => {
            onKeywordClick?.(keyword, state);
            onClose();
        },
        [onClose, onKeywordClick],
    );

    const onWebsiteItemClick = useCallback<OnSearchItemClick>(
        (website, state) => {
            onWebsiteClick?.(website, state);
            onClose();
        },
        [onClose, onWebsiteClick],
    );

    const accessibleCatalogPages = useAccessibleQuickSearchCatalogPages(getLocale());

    const [reportItemsForReportsTab, reportItemsForAllTab] = useMemo(() => {
        let reportPages = accessibleCatalogPages.slice(0, REPORTS_TAB_ITEM_COUNT);

        const recentMatched = recentAll
            .filter((r) => r.data.stateName)
            .map((r) => accessibleCatalogPages.find((page) => page.uiRouterState === r.data.stateName))
            .filter((p): p is NonNullable<typeof p> => Boolean(p));

        const uniqueRecentMatched = Array.from(new Map(recentMatched.map((item) => [item.id, item])).values());

        if (uniqueRecentMatched.length > 0) {
            reportPages = uniqueRecentMatched.slice(0, 10);
        }

        const buildRows = (instance: "reports" | "all") =>
            reportPages.map((page, index) => (
                <QuickSearchReportRow
                    key={`${instance}-${page.id}`}
                    title={page.title}
                    description={page.breadcrumbs.join(" > ")}
                    href={page.url}
                    isExternal={page.isExternal}
                    isLocked={page.isLocked}
                    dataAutomation={
                        instance === "reports"
                            ? quickSearchDataAutomation.reportRowReportsTab(index)
                            : quickSearchDataAutomation.reportRowAllTab(index)
                    }
                    onClick={() => {
                        onReportClick?.(page.title, page.isLocked ? "locked" : "recent");
                        navigateCatalogPage(page);
                        onClose();
                    }}
                />
            ));

        return [buildRows("reports"), buildRows("all")] as const;
    }, [accessibleCatalogPages, onClose, onReportClick, recentAll]);

    const getDataRenderItemsTabs = useCallback(
        async (query: string) => {
            const trimmedQuery = query.trim();
            setListScrollResetKey((previousKey) => (previousKey === trimmedQuery ? previousKey : trimmedQuery));
            const tab = TAB_LIST[tabIndex];
            const searchKey = { query: trimmedQuery, tab };

            latestSearchRef.current = searchKey;

            const websites = isWebsitesLocked
                ? []
                : await getWebsitesItems({
                      query,
                      recentWebsites,
                      onClick: onWebsiteItemClick,
                  });

            const keywords = isKeywordsLocked
                ? []
                : await getKeywordsItems({
                      query,
                      recentKeywords,
                      onClick: onKeywordItemClick,
                  });

            // Skip tracking if the user typed again while this request was in flight (only the latest search should be tracked).
            const isLatestSearch =
                latestSearchRef.current?.query === searchKey.query && latestSearchRef.current?.tab === searchKey.tab;
            const isNewUserQuery =
                trimmedQuery.length >= MIN_TRACKED_QUERY_LENGTH && trimmedQuery !== lastTrackedQueryRef.current;

            if (isLatestSearch && isNewUserQuery) {
                lastTrackedQueryRef.current = trimmedQuery;
                if (onTrackUserQuery) {
                    onTrackUserQuery(trimmedQuery, tab);
                } else {
                    quickSearchTracking.trackUserQuery(trimmedQuery, tab);
                }
            }

            const websiteItems = Array.isArray(websites) ? websites : [];
            const keywordItems = Array.isArray(keywords) ? keywords : [];

            const websiteTabItems =
                websiteItems.length > 0
                    ? websiteItems
                    : [
                          <QuickSearchTabEmptyState
                              key="quick-search-empty-website"
                              searchQuery={query}
                              dataAutomation={quickSearchDataAutomation.tabEmptyWebsite}
                          />,
                      ];
            const keywordTabItems =
                keywordItems.length > 0
                    ? keywordItems
                    : [
                          <QuickSearchTabEmptyState
                              key="quick-search-empty-keyword"
                              dataAutomation={quickSearchDataAutomation.tabEmptyKeyword}
                              searchQuery={query}
                          />,
                      ];

            return {
                [SearchTabs.All]: [
                    <QuickSearchAllTabPanel
                        key={`quick-search-all-${query}`}
                        websiteItems={websiteItems}
                        keywordItems={keywordItems}
                        reportItems={reportItemsForAllTab}
                        searchQuery={query}
                        onCatalogReportNavigate={(page: QuickSearchCatalogPage, state: string) => {
                            onReportClick?.(page.title, state);
                            navigateCatalogPage(page);
                            onClose();
                        }}
                    />,
                ],
                [SearchTabs.Website]: websiteTabItems,
                [SearchTabs.Keyword]: keywordTabItems,
                [SearchTabs.Reports]: [
                    <QuickSearchReportsTabPanel
                        key={`quick-search-reports-${query}`}
                        searchQuery={query}
                        defaultReportRows={reportItemsForReportsTab}
                        onCatalogReportNavigate={(page: QuickSearchCatalogPage, state: string) => {
                            onReportClick?.(page.title, state);
                            navigateCatalogPage(page);
                            onClose();
                        }}
                    />,
                ],
            };
        },
        [
            recentWebsites,
            recentKeywords,
            isWebsitesLocked,
            isKeywordsLocked,
            onWebsiteItemClick,
            onKeywordItemClick,
            tabIndex,
            onTrackUserQuery,
        ],
    );

    const onSelectTab = useCallback(
        (tabInx: number) => {
            setTabIndex(tabInx);

            onTabClick?.(TAB_LIST[tabInx]);
        },
        [onTabClick],
    );

    const renderItems = ({ listItems }: { listItems: Record<SearchTabs, JSX.Element[]> }) => (
        <ItemTabs
            selectedTab={tabIndex}
            onSelectTab={onSelectTab}
            listItems={listItems}
            isKeywordsLocked={isKeywordsLocked}
            isWebsitesLocked={isWebsitesLocked}
            isLoading={false}
            scrollAreaMaxHeightPx={tabPanelScrollMaxHeightPx}
            scrollAreaMinHeightPx={tabPanelScrollMinHeightPx}
            scrollResetKey={listScrollResetKey}
        />
    );

    return {
        isRecentReady,
        isWsLoading,
        isKwLoading,
        isAllLoading,
        recentsVersion,
        getPlaceholder,
        getDataRenderItemsTabs,
        renderItems,
        isKeywordsLocked,
        isWebsitesLocked,
    };
};
